import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateCase, deleteCase } from '@/api/services/casesApi';
import type { CreateCaseRequest } from '@/types/api';

/**
 * Mutation hook for updating a tuition case
 * Parents use this to edit their existing cases
 * Invalidates 'cases', 'parentCases', and individual case caches on success
 */
export const useUpdateCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateCaseRequest }) =>
      updateCase(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate public cases, parent cases list, and specific case detail
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['parentCases'] });
      queryClient.invalidateQueries({ queryKey: ['case', id] });
    },
    retry: 1,
  });
};

/**
 * Mutation hook for deleting a tuition case
 * Parents use this to delete their own cases
 * Invalidates both 'cases' and 'parentCases' cache on success
 */
export const useDeleteCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteCase(id),
    onSuccess: () => {
      // Invalidate both public and parent-specific case lists
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['parentCases'] });
    },
    retry: 1,
  });
};
